<template>
    <label
        :style="{ display: inline ? 'inline' : 'block' }"
        :class="{ 'kgr-inputGroup': label !== null, 'kgr-inputGroup--inline': label !== null && inline }"
    >
        <div
            v-if="label !== null"
            class="kgr-inputGroup__label"
        >
            {{ label }}
        </div>
        <div
            class="kgr-searchable-dropdown"
        >
            <input
                ref="searchInput"
                v-model="searchFilter"
                class="kgr-select"
                :disabled="disabled"
                :placeholder="$t(placeholderText)"
                @focus="showOptions()"
                @blur="exit()"
                @keydown="keyMonitor"
            />
            <button
                v-if="clearable && selected"
                class="kgr-searchable-dropdown__clear"
                @mousedown.prevent
                @click.stop="clearSelection"
            >
                <kgr-icon
                    icon-class="clear"
                />

            </button>

            <div
                v-if="optionsShown"
                ref="dropdownContent"
                class="kgr-searchable-dropdown__content"
            >
                <kgr-loading
                    v-if="loading"
                    inline
                />
                <div
                    v-for="(option, index) in filteredOptions"
                    v-show="!loading"
                    ref="dropdownOption"
                    :key="index"
                    class="kgr-searchable-dropdown__item"
                    :class="{'is-active': activeIndex === index}"
                    @mousedown="selectOption(option)"
                >
                    {{ displayOption(option) }}
                </div>
                <div
                    v-if="hasMoreOptions"
                    class="kgr-searchable-dropdown__item"
                    @mousedown.prevent="increaseMaxItems()"
                >
                    <span class="kgr-text--bold">{{ $t('mehr') }}</span>
                </div>
            </div>
        </div>
    </label>
</template>

<script>
import Icon from '../../../common/icon/Icon.vue';
import Loading from '../../Loading.vue';

export default {
    components: {
        'kgr-loading': Loading,
        'kgr-icon': Icon,
    },
    props: {
        options: {
            type: Array,
            required: true,
        },
        loading: {
            type: Boolean,
            required: false,
            default: false,
        },
        label: {
            type: String,
            required: false,
            default: null,
        },
        inline: {
            type: Boolean,
            required: false,
            default: false,
        },
        display: {
            type: String,
            required: false,
            default: null
        },
        placeholder: {
            type: String,
            required: false,
            default: undefined,
        },
        disabled: {
            type: Boolean,
            required: false,
            default: false,
        },
        clearAfterSelect: {
            type: Boolean,
            required: false,
            default: false,
        },
        maxItems: {
            type: Number,
            required: false,
            default: 15,
        },
        initialOption: {
            type: [Object, String],
            required: false,
            default: null
        },
        clearable: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    data() {
        return {
            selected: null,
            optionsShown: false,
            searchFilter: '',
            activeIndex: -1,
            visibleOptions: this.maxItems,
            suppressWatcher: false,
            clearing: false,
        }
    },
    computed: {
        filteredOptions() {
            const filtered = [];
            const regOption = new RegExp(this.searchFilter.replace(/[/\-\\^$*+?.()|[\]{}]/g, '\\$&'), 'ig');
            for (const option of this.options) {
                const display = this.displayOption(option);
                if (this.searchFilter.length < 1 || display.match(regOption)){
                    if (filtered.length < this.visibleOptions) {
                        filtered.push(option);
                    }
                }
            }
            return filtered;
        },
        hasMoreOptions() {
            return (this.searchFilter.length < 1 && this.options.length > this.visibleOptions)
                || (this.filteredOptions.length >= this.visibleOptions);
        },
        placeholderText() {
            return this.placeholder ? this.placeholder : 'search';
        },
        scrollPosition() {
            if(!this.$refs.dropdownOption[this.activeIndex]) {
                return 0;
            }
            if(this.$refs.dropdownOption[this.activeIndex].offsetTop > 110) {
                return this.$refs.dropdownOption[this.activeIndex].offsetTop - 110;

            }
            return 0;
        }

    },
    watch: {
        searchFilter(newValue) {
            if (this.suppressWatcher) {
                this.suppressWatcher = false;
                return;
            }
            if (this.clearing) {
                this.optionsShown = false;
                return;
            }
            if(newValue !== this.displayOption(this.selected)) {
                this.$emit('inputChanged', newValue);
                this.optionsShown = true;
            }
        }
    },
    created() {
        if(this.initialOption !== null) {
            this.selectOption(this.initialOption);
        }
    },
    methods: {
        selectOption(option) {
            this.selected = option;
            if(this.clearAfterSelect) {
                this.searchFilter = '';
                this.selected = null;
            } else {
                this.searchFilter = this.displayOption(option);
            }
            this.optionsShown = false;

            this.$emit('input', option);
            this.$nextTick(() => {
                this.$emit('post-input', option);
            })
        },
        showOptions(){
            if (!this.disabled) {
                this.activeIndex = -1;
                this.optionsShown = true;
            }
        },
        increaseMaxItems(){
            this.visibleOptions += 10;
        },
        exit() {
            if (!this.selected || this.clearAfterSelect) {
                this.selected = null;
                this.searchFilter = '';
            } else {
                this.searchFilter = this.displayOption(this.selected);
            }
            this.optionsShown = false;
        },
        keyMonitor: function(event) {
            if (event.key === 'ArrowDown') {
                if(!this.optionsShown) {
                    this.searchFilter = '';
                    this.optionsShown = true;
                }
                this.activeIndex++;
                if(this.activeIndex >= this.filteredOptions.length) {
                    this.activeIndex = 0;
                }

                if(this.$refs.dropdownContent) {
                    this.$refs.dropdownContent.scrollTop = this.scrollPosition;
                }
            }
            if (event.key === 'ArrowUp') {
                this.activeIndex--;
                if(this.activeIndex === -1) {
                    this.activeIndex = this.filteredOptions.length - 1;
                }
                if(this.$refs.dropdownContent) {
                    this.$refs.dropdownContent.scrollTop = this.scrollPosition;
                }
            }
            if (event.key === 'Enter' && this.filteredOptions[this.activeIndex]) {
                this.selectOption(this.filteredOptions[this.activeIndex])
            }
        },
        displayOption(option) {
            if(!option) {
                return '';
            }
            if (this.display) {
                return typeof option[this.display] === 'function' ? option[this.display]() : option[this.display];
            }

            return this.$t(option);
        },
        clearSelection() {
            this.suppressWatcher = true;
            this.clearing = true;
            this.selected = null;
            this.searchFilter = '';
            this.$emit('input', null);
            this.$nextTick(() => {
                this.$emit('post-input', null);
            })
            this.optionsShown = false;
            setTimeout(() => {
                if (this.$refs.searchInput) {
                    this.$refs.searchInput.blur();
                }
                this.clearing = false;
            }, 0);
        },
    },
}
</script>

<style rel="stylesheet/scss" lang="scss" scoped>
  .kgr-searchable-dropdown {
    position: relative;
    display: block;
    margin: auto;
    .dropdown-input {
      background: #fff;
      cursor: pointer;
      border: 1px solid #e7ecf5;
      border-radius: 3px;
      color: #333;
      display: block;
      font-size: .8em;
      padding: 6px;
      min-width: 250px;
      max-width: 250px;
      &:hover {
        background: #f8f8fa;
      }
    }
    .kgr-searchable-dropdown__content {
      position: absolute;
      background-color: var(--kgr-input-background-color);
      width: 100%;
      max-height: 248px;
      border: 1px solid #000;
      box-shadow: 0px -8px 34px 0px rgba(0,0,0,0.05);
      overflow-y: auto;
      z-index: 1;
      .kgr-searchable-dropdown__item {
        color: black;
        font-size: var(--kgr-input-fontSize);
        line-height: 1em;
        padding: 4px;
        text-decoration: none;
        display: block;
        cursor: pointer;
        &:hover {
          background-color: #e7ecf5;
        }
        &.is-active {
          background-color: #e7ecf5;
        }
      }
    }
    .kgr-searchable-dropdown:hover .kgr-searchable-dropdown__content {
      display: block;
    }
    .kgr-searchable-dropdown__clear {
        position: absolute;
        right: 35px;
        top: 15px;
    }
  }
</style>
